import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, ClickAwayListener, Paper, Popper, Typography, useTheme } from '@mui/material';
import React, { useState, useRef, useMemo, useCallback } from 'react';

import { MultiSelectDropdownProps } from './FilterBar.props';
import {
  controlBaseSx,
  controlEngagedSx,
  CONTROL_FONT_SIZE,
  dropdownSurfaceSx,
  POPPER_SHADOW,
  SURFACE_RADIUS,
  valuePillSx,
} from './filterBarStyles';
import { GroupHeader, OptionRow } from './MultiSelectDropdownRows';
import { MultiSelectDropdownSearch } from './MultiSelectDropdownSearch';

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  groups,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  /**
   * What the badge carries: the value itself when exactly one is picked, the
   * count past that.
   *
   * The badge rather than the button text, so the field name stays put and the
   * value reads as *applied* — swapping the label for the value loses which
   * field is being filtered, and a bare word in the button looks like a
   * placeholder rather than a live filter.
   */
  const badgeContent = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length > 1) return String(selected.length);
    return options.find((o) => o.value === selected[0])?.label ?? selected[0];
  }, [selected, options]);
  /** Any selection at all puts the control into its engaged state. */
  const engaged = selected.length > 0;
  const anchorRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(() => {
    const needle = search.toLowerCase();
    return options.filter((o) => (o.searchText ?? o.label).toLowerCase().includes(needle));
  }, [options, search]);

  const handleToggle = useCallback(
    (value: string) => {
      const next = selected.includes(value)
        ? selected.filter((s) => s !== value)
        : [...selected, value];
      onChange(next);
    },
    [selected, onChange]
  );

  // Deduplicate: two options sharing the same value (e.g. duplicate status
  // names) must not produce two entries in the selected array.
  const handleSelectAll = () => onChange([...new Set(filtered.map((o) => o.value))]);
  const handleClear = () => onChange([]);

  const accent = theme.palette.v4.accent;
  const hoverBg = theme.palette.action.hover;

  const renderGroupedOptions = () => {
    if (!groups) return null;
    return groups.map((group) => {
      const groupOptions = filtered.filter((o) => group.values.includes(o.value));
      if (groupOptions.length === 0) return null;
      return (
        <Box key={group.name}>
          <GroupHeader group={group} selected={selected} onChange={onChange} />
          {groupOptions.map((opt) => (
            <OptionRow
              key={opt.value}
              label={opt.label}
              checked={selected.includes(opt.value)}
              onClick={() => handleToggle(opt.value)}
              accentColor={accent}
              hoverBg={hoverBg}
            />
          ))}
        </Box>
      );
    });
  };

  const renderFlatOptions = () =>
    filtered.map((opt) => (
      <OptionRow
        key={opt.value}
        label={opt.label}
        checked={selected.includes(opt.value)}
        onClick={() => handleToggle(opt.value)}
        accentColor={accent}
        hoverBg={hoverBg}
      />
    ));

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative' }}>
        <Button
          ref={anchorRef}
          onClick={() => setOpen((p) => !p)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label} filter`}
          sx={{
            ...controlBaseSx(theme),
            ...dropdownSurfaceSx(theme),
            ...(engaged ? controlEngagedSx(theme) : {}),
            textTransform: 'none',
            minWidth: 118,
            justifyContent: 'space-between',
            gap: 0.75,
            '& .MuiButton-endIcon': { ml: 0, color: theme.palette.v4.textFaint },
            // Open is a state, not focus — the border alone says so. Leaving
            // the ring to `:focus-visible` keeps the two readable apart.
            ...(open && { borderColor: accent }),
          }}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                transition: 'transform 140ms ease',
                ...(open && { transform: 'rotate(180deg)' }),
              }}
            />
          }
        >
          <Typography
            component="span"
            noWrap
            sx={{
              fontSize: CONTROL_FONT_SIZE,
              fontWeight: 500,
              color: theme.palette.v4.textMuted,
            }}
          >
            {label}
          </Typography>
          {badgeContent !== null && (
            <Box
              component="span"
              sx={{
                ...valuePillSx(theme),
                ml: 'auto',
                maxWidth: 132,
                textAlign: 'center',
                // A value can be long where a count never is; clip rather than
                // let one pick stretch the control past its neighbours.
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {badgeContent}
            </Box>
          )}
        </Button>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: theme.zIndex.modal + 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: 264,
              maxHeight: 380,
              display: 'flex',
              flexDirection: 'column',
              mt: 0.75,
              overflow: 'hidden',
              borderRadius: SURFACE_RADIUS,
              border: `1px solid ${theme.palette.v4.border}`,
              boxShadow: POPPER_SHADOW,
            }}
          >
            <MultiSelectDropdownSearch
              label={label}
              search={search}
              selectedCount={selected.length}
              onSearchChange={setSearch}
              onSelectAll={handleSelectAll}
              onClear={handleClear}
            />

            <Box
              role="listbox"
              aria-label={`${label} options`}
              sx={{ overflowY: 'auto', maxHeight: 280, px: 0.75, py: 0.5 }}
            >
              {groups ? renderGroupedOptions() : renderFlatOptions()}
              {filtered.length === 0 && (
                <Typography
                  sx={{ px: 1, py: 1.25, fontSize: '12px', color: theme.palette.v4.textFaint }}
                >
                  {search ? `No options match “${search}”` : 'No options to choose from'}
                </Typography>
              )}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
